import Form from "common/Form/Form";
import Input from "common/Inputs/Input";
import { SizeType } from "antd/es/config-provider/SizeContext";
import Text from "common/Typography/Text";
import CheckOutlined from "@ant-design/icons/CheckOutlined";
import CloseOutlined from "@ant-design/icons/CloseOutlined";
import Flex from "common/Boxes/Flex";
import { motion } from "motion/react";
import usePasswordSection, {
  ValidationItemData,
} from "@shared/features/onboarding/hooks/usePasswordSection";

interface PasswordSectionProps {
  size?: SizeType;
  showConfirm?: boolean;
  showPlaceholder?: boolean;
  passwordLabel?: string;
}

export const PasswordSection = ({
  size,
  showConfirm,
  showPlaceholder = true,
  passwordLabel,
}: PasswordSectionProps) => {
  const {
    onPasswordChange,
    onFocus,
    validationTitle,
    validationData,
    onBlur,
    showPasswordValidation,
  } = usePasswordSection();

  const checkPasswordCondition = (validationItem: ValidationItemData) => {
    const style = { color: validationItem.color };
    if (validationItem.isValid) {
      return <CheckOutlined style={style} />;
    }
    return <CloseOutlined style={style} />;
  };

  const getPasswordErrorMessage = () => {
    return (
      <motion.div
        initial={{ height: 0, opacity: 0 }}
        animate={{ height: "auto", opacity: 1 }}
        transition={{ duration: 0.4 }}
      >
        <Text type="secondary" style={{ margin: "8px 0" }}>
          {validationTitle}
        </Text>
        <div style={{ margin: "0 0 16px 24px" }}>
          {validationData.map((itemData, index) => (
            <Flex
              key={index}
              $justifyContent="flex-start"
              style={{ marginBottom: 8 }}
            >
              {checkPasswordCondition(itemData)}
              <Text style={{ marginLeft: 8 }}>{itemData.text}</Text>
            </Flex>
          ))}
        </div>
      </motion.div>
    );
  };

  return (
    <>
      <Form.Item
        name="password"
        label={passwordLabel || "Password"}
        style={{ margin: "0 0 16px" }}
        rules={[
          {
            validator: (_, value) => {
              const isValid = onPasswordChange(value);
              if (isValid) return Promise.resolve();
            },
          },
        ]}
      >
        <Input.Password
          placeholder={showPlaceholder ? "Password" : undefined}
          size={size}
          onBlur={onBlur}
          onFocus={onFocus}
        />
      </Form.Item>
      {showPasswordValidation && getPasswordErrorMessage()}

      {showConfirm && (
        <Form.Item
          name="confirmPassword"
          label={`Confirm ${passwordLabel || "Password"}`}
          dependencies={["password"]}
          hasFeedback
          rules={[
            {
              required: true,
              message: "Please confirm your password!",
            },
            ({ getFieldValue }) => ({
              validator(_, value) {
                if (!value || getFieldValue("password") === value) {
                  return Promise.resolve();
                }
                return Promise.reject(
                  "The two passwords that you entered do not match!"
                );
              },
            }),
          ]}
        >
          <Input.Password
            placeholder={showPlaceholder ? "Confirm Password" : undefined}
            size={size}
          />
        </Form.Item>
      )}
    </>
  );
};
